import { useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createEvent,
  deleteEvent,
  listCalendar,
  type CalendarEntry,
  type CreateHotelEventBody,
} from '../features/hotels/api.js';
import {
  extractEventsFromFile,
  extractEventsFromUrl,
  listEventCandidates,
  reviewEventCandidates,
  type HotelEventCandidate,
} from '../features/hotels/events-extraction.api.js';
import {
  groupEntriesByDay,
  showsSpanTitle,
  spanPosition,
} from '../features/hotels/calendar-days.js';
import { EventEditModal } from '../features/hotels/event-edit-modal.js';
import { cn } from '../lib/utils.js';
import { Input } from '../components/ui/input.js';
import { Button } from '../components/ui/button.js';
import { Textarea } from '../components/ui/textarea.js';
import { Dialog } from '../components/ui/dialog.js';
import { Badge, type BadgeTone } from '../components/ui/badge.js';
import { useConfirm } from '../components/ui/confirm-dialog.js';

const HU_MONTH_LONG = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];

const HU_DAYS_SHORT = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

/** Tailwind / token-style for each calendar entry kind. */
const KIND_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
  holiday: { bg: 'var(--cal-holiday-bg)', fg: 'var(--cal-holiday-fg)', label: 'Ünnep' },
  long_weekend: {
    bg: 'var(--cal-long-weekend-bg)',
    fg: 'var(--cal-long-weekend-fg)',
    label: 'Hosszú hétvége',
  },
  commercial: {
    bg: 'var(--cal-commercial-bg)',
    fg: 'var(--cal-commercial-fg)',
    label: 'Kereskedelmi',
  },
  observance: {
    bg: 'var(--cal-observance-bg)',
    fg: 'var(--cal-observance-fg)',
    label: 'Jeles nap',
  },
  season: { bg: 'var(--cal-season-bg)', fg: 'var(--cal-season-fg)', label: 'Évszak' },
  event: { bg: 'var(--brand-100)', fg: 'var(--brand-800)', label: 'Hotel esemény' },
  promo: { bg: 'var(--cal-promo-bg)', fg: 'var(--cal-promo-fg)', label: 'Hotel akció' },
  seasonal: { bg: 'var(--cal-season-bg)', fg: 'var(--cal-season-fg)', label: 'Hotel szezon' },
};

const DEFAULT_STYLE = { bg: 'var(--brand-100)', fg: 'var(--brand-800)', label: 'Egyéb' };

function kindStyle(kind: string): { bg: string; fg: string; label: string } {
  return KIND_STYLE[kind] ?? DEFAULT_STYLE;
}

function HotelEventsPage() {
  const { hotelId } = Route.useParams();
  const confirmDialog = useConfirm();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-indexed

  const { from, to } = useMemo(() => monthBounds(year, month), [year, month]);

  const qc = useQueryClient();
  const calendar = useQuery<CalendarEntry[]>({
    queryKey: ['hotel-calendar', hotelId, from, to],
    queryFn: () => listCalendar(hotelId, from, to),
  });

  const [addForDate, setAddForDate] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<CalendarEntry | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  // Once an extraction job is kicked off we poll the candidates endpoint.
  const [polling, setPolling] = useState(false);
  const [pollStartedAt, setPollStartedAt] = useState<number | null>(null);

  const candidates = useQuery<HotelEventCandidate[]>({
    queryKey: ['hotel-event-candidates', hotelId],
    queryFn: () => listEventCandidates(hotelId),
    refetchInterval: () => {
      if (!polling) return false;
      return 3000;
    },
  });

  // Stop polling once candidates arrive or after ~30s.
  if (polling) {
    const hasRows = (candidates.data ?? []).length > 0;
    const timedOut = pollStartedAt != null && Date.now() - pollStartedAt > 30_000;
    if (hasRows || timedOut) {
      setPolling(false);
      setPollStartedAt(null);
    }
  }

  function startPolling() {
    setPollStartedAt(Date.now());
    setPolling(true);
  }

  function goPrev() {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  }
  function goNext() {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  }
  function goToday() {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
  }

  // Indexed by every day an entry covers, so a multi-day event is findable on
  // each of its days — not just the one it started on.
  const entriesByDate = useMemo(() => groupEntriesByDay(calendar.data ?? []), [calendar.data]);

  const monthDays = useMemo(() => buildMonthGrid(year, month), [year, month]);

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--ink-700)] hover:bg-[var(--app-hover)]"
            aria-label="Előző hónap"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={goNext}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--ink-700)] hover:bg-[var(--app-hover)]"
            aria-label="Következő hónap"
          >
            ›
          </button>
          <Button size="sm" variant="outline" onClick={goToday}>
            Ma
          </Button>
          <h2 className="ml-2 text-h2 font-semibold text-[var(--ink-700)]">
            <span className="tn">{year}.</span> {HU_MONTH_LONG[month - 1]}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <LegendBar />
          <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
            Importálás
          </Button>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="overflow-hidden rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)]">
        <div className="grid grid-cols-7 border-b border-[var(--app-divider)] bg-[var(--app-surface-2)] text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
          {HU_DAYS_SHORT.map((d) => (
            <div key={d} className="px-2 py-1.5 text-center">
              {d}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {monthDays.map((d, idx) => {
            const iso = d.iso;
            const entries = entriesByDate.get(iso) ?? [];
            const inMonth = d.inMonth;
            return (
              <div
                key={`${iso}-${idx}`}
                className={`group relative min-h-[100px] border-b border-r border-[var(--app-divider)] p-1.5 ${
                  inMonth ? 'bg-[var(--app-surface)]' : 'bg-[var(--app-surface-2)] opacity-60'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-meta tn ${d.isToday ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-[var(--brand-500)] text-white' : 'text-[var(--ink-700)]'} ${!inMonth ? 'text-[var(--fg-faint)]' : ''}`}
                  >
                    {d.day}
                  </span>
                  {inMonth && (
                    <button
                      type="button"
                      onClick={() => setAddForDate(iso)}
                      className="hidden h-5 w-5 items-center justify-center rounded-full bg-[var(--brand-100)] text-body leading-none text-[var(--brand-800)] hover:bg-[var(--brand-200)] group-hover:flex"
                      aria-label="Új esemény"
                      title="Új esemény"
                    >
                      +
                    </button>
                  )}
                </div>
                <div className="mt-1 space-y-1">
                  {entries.map((e) => (
                    <EntryPill
                      key={e.id}
                      entry={e}
                      day={iso}
                      onOpen={() => setEditingEntry(e)}
                      onDelete={async () => {
                        if (e.source !== 'hotel') return;
                        const ok = await confirmDialog({
                          title: 'Esemény törlése',
                          message: e.title,
                          confirmLabel: 'Törlés',
                          tone: 'destructive',
                        });
                        if (!ok) return;
                        deleteEvent(hotelId, e.id).then(() => {
                          void qc.invalidateQueries({
                            queryKey: ['hotel-calendar', hotelId],
                          });
                        });
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {polling && (candidates.data ?? []).length === 0 && (
        <div className="rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-4 py-3 text-body text-[var(--fg-2)]">
          Feldolgozás folyamatban…
        </div>
      )}

      {(candidates.data ?? []).length > 0 && (
        <CandidateReview
          hotelId={hotelId}
          candidates={candidates.data ?? []}
          onReviewed={() => {
            void qc.invalidateQueries({ queryKey: ['hotel-event-candidates', hotelId] });
            void qc.invalidateQueries({ queryKey: ['hotel-calendar', hotelId] });
          }}
        />
      )}

      {addForDate && (
        <AddEventModal
          hotelId={hotelId}
          date={addForDate}
          onOpenImport={() => {
            setAddForDate(null);
            setImportOpen(true);
          }}
          onClose={() => setAddForDate(null)}
          onCreated={() => {
            setAddForDate(null);
            void qc.invalidateQueries({ queryKey: ['hotel-calendar', hotelId] });
          }}
        />
      )}

      {editingEntry && (
        <EventEditModal
          hotelId={hotelId}
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={() => {
            setEditingEntry(null);
            void qc.invalidateQueries({ queryKey: ['hotel-calendar', hotelId] });
          }}
        />
      )}

      <ImportEventsDialog
        hotelId={hotelId}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onStarted={() => {
          setImportOpen(false);
          startPolling();
        }}
      />
    </div>
  );
}

const SOURCE_BADGE: Record<string, { tone: BadgeTone; label: string }> = {
  url: { tone: 'info', label: 'Weboldal' },
  pdf: { tone: 'warn', label: 'PDF' },
  spreadsheet: { tone: 'success', label: 'Táblázat' },
  image: { tone: 'ai', label: 'Kép' },
};

function ImportEventsDialog({
  hotelId,
  open,
  onClose,
  onStarted,
}: {
  hotelId: string;
  open: boolean;
  onClose: () => void;
  onStarted: () => void;
}) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const urlMut = useMutation({
    mutationFn: () => extractEventsFromUrl(hotelId, url.trim()),
    onSuccess: () => {
      setUrl('');
      setError(null);
      onStarted();
    },
    onError: (e: Error) => setError(e.message),
  });

  const fileMut = useMutation({
    mutationFn: (file: File) => extractEventsFromFile(hotelId, file),
    onSuccess: () => {
      setError(null);
      onStarted();
    },
    onError: (e: Error) => setError(e.message),
  });

  const busy = urlMut.isPending || fileMut.isPending;

  return (
    <Dialog open={open} onClose={onClose} title="Események importálása" size="md">
      {/* w-full, never a fixed px width: the shell is max-w-md, and a wider
          inner box pushed the field row, the separator and the drop zone past
          the panel edge. */}
      <div className="w-full space-y-5">
        <form
          className="space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!url.trim()) return;
            urlMut.mutate();
          }}
        >
          <label className="block text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
            Weboldal címe
          </label>
          <div className="flex items-center gap-2">
            <Input
              type="url"
              placeholder="https://…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={busy}
              className="min-w-0 flex-1"
            />
            <Button type="submit" size="sm" disabled={busy || !url.trim()} className="shrink-0">
              {urlMut.isPending ? 'Letöltés…' : 'Letöltés'}
            </Button>
          </div>
        </form>

        <div className="flex items-center gap-3 text-meta text-[var(--fg-faint)]">
          <span className="h-px flex-1 bg-[var(--app-divider)]" />
          vagy
          <span className="h-px flex-1 bg-[var(--app-divider)]" />
        </div>

        <div className="space-y-2">
          <label className="block text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
            Fájl feltöltése
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.xlsx,.xls,.csv,image/*"
            disabled={busy}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) fileMut.mutate(file);
              // Reset so picking the same file again re-triggers onChange.
              e.target.value = '';
            }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-[var(--app-divider)] bg-[var(--app-surface-2)] px-4 py-6 text-body text-[var(--fg-2)] hover:bg-[var(--app-hover)] disabled:opacity-60"
          >
            <span className="font-medium text-[var(--ink-700)]">
              {fileMut.isPending ? 'Feltöltés…' : 'Fájl kiválasztása'}
            </span>
            <span className="text-meta text-[var(--fg-faint)]">PDF, XLSX, XLS, CSV vagy kép</span>
          </button>
        </div>

        {error && <p className="text-meta text-[var(--danger)]">{error}</p>}

        <div className="flex items-center justify-end pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Bezárás
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function CandidateReview({
  hotelId,
  candidates,
  onReviewed,
}: {
  hotelId: string;
  candidates: HotelEventCandidate[];
  onReviewed: () => void;
}) {
  // Track which candidate ids are checked. Default: all checked.
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(candidates.map((c) => [c.id, true])),
  );
  const [error, setError] = useState<string | null>(null);

  // Keep the checked map in sync when the candidate set changes (new rows
  // default to checked; gone rows are pruned).
  const idsKey = candidates.map((c) => c.id).join(',');
  const [lastIdsKey, setLastIdsKey] = useState(idsKey);
  if (idsKey !== lastIdsKey) {
    setChecked((prev) => {
      const next: Record<string, boolean> = {};
      for (const c of candidates) next[c.id] = prev[c.id] ?? true;
      return next;
    });
    setLastIdsKey(idsKey);
  }

  const checkedIds = candidates.filter((c) => checked[c.id]).map((c) => c.id);
  const uncheckedIds = candidates.filter((c) => !checked[c.id]).map((c) => c.id);

  const review = useMutation({
    mutationFn: (body: { acceptIds: string[]; rejectIds: string[] }) =>
      reviewEventCandidates(hotelId, body),
    onSuccess: () => {
      setError(null);
      onReviewed();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="space-y-3 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-h2 font-semibold text-[var(--ink-700)]">
          Importált jelöltek{' '}
          <span className="text-meta font-normal text-[var(--fg-faint)]">
            ({candidates.length} db)
          </span>
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={review.isPending}
            onClick={() => review.mutate({ acceptIds: candidates.map((c) => c.id), rejectIds: [] })}
          >
            Mindet elfogadom
          </Button>
          <Button
            size="sm"
            disabled={review.isPending || checkedIds.length === 0}
            onClick={() => review.mutate({ acceptIds: checkedIds, rejectIds: [] })}
          >
            Kiválasztottak elfogadása
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={review.isPending || uncheckedIds.length === 0}
            onClick={() => review.mutate({ acceptIds: [], rejectIds: uncheckedIds })}
          >
            Nem jelöltek elvetése
          </Button>
        </div>
      </div>

      {error && <p className="text-meta text-[var(--danger)]">{error}</p>}

      <div className="overflow-hidden rounded-md border border-[var(--app-divider)]">
        <table className="w-full text-body">
          <thead className="bg-[var(--app-surface-2)] text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
            <tr>
              <th className="w-10 px-2 py-2" />
              <th className="px-2 py-2 text-left">Dátum</th>
              <th className="px-2 py-2 text-left">Cím</th>
              <th className="px-2 py-2 text-left">Leírás</th>
              <th className="px-2 py-2 text-left">Forrás</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => {
              const badge = SOURCE_BADGE[c.source] ?? {
                tone: 'neutral' as BadgeTone,
                label: c.source,
              };
              const dateLabel =
                c.endsOn && c.endsOn !== c.startsOn ? `${c.startsOn} – ${c.endsOn}` : c.startsOn;
              return (
                <tr key={c.id} className="border-t border-[var(--app-divider)] align-top">
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={checked[c.id] ?? false}
                      onChange={(e) =>
                        setChecked((prev) => ({ ...prev, [c.id]: e.target.checked }))
                      }
                      aria-label={`${c.title} kiválasztása`}
                    />
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 tn text-[var(--ink-700)]">
                    {dateLabel}
                  </td>
                  <td className="px-2 py-2 font-medium text-[var(--ink-700)]">{c.title}</td>
                  <td className="px-2 py-2 text-[var(--fg-2)]">{c.description ?? '—'}</td>
                  <td className="px-2 py-2">
                    <Badge tone={badge.tone}>{badge.label}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LegendBar() {
  const items: { kind: string; label: string; description: string }[] = [
    { kind: 'holiday', label: 'Ünnep', description: 'Nemzeti / vallási ünnep' },
    { kind: 'long_weekend', label: 'Hosszú hétvége', description: 'Több napos szabadnap' },
    { kind: 'commercial', label: 'Kereskedelmi', description: 'Pl. Black Friday, Valentin' },
    { kind: 'observance', label: 'Jeles nap', description: 'Tematikus emléknap' },
    { kind: 'event', label: 'Hotel esemény', description: 'A szálloda saját eseménye' },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-meta text-[var(--fg-muted)]">
      {items.map((i) => {
        const s = kindStyle(i.kind);
        return (
          <span key={i.kind} className="inline-flex items-center gap-1.5" title={i.description}>
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: s.bg, border: `1px solid ${s.fg}` }}
              aria-hidden
            />
            <span>{i.label}</span>
          </span>
        );
      })}
    </div>
  );
}

/**
 * One day's segment of a calendar entry.
 *
 * A multi-day event is indexed on every day it covers, so without span-aware
 * styling it read as several separate events sitting next to each other. The
 * segments now join into one continuous bar: only the outer edges are rounded,
 * the segments bleed into the cell padding so they touch across day borders,
 * and the title + delete action live on the leading segment (repeated on a
 * Monday, where the bar restarts in a new week row). Clicking any segment
 * opens the same event.
 */
function EntryPill({
  entry,
  day,
  onDelete,
  onOpen,
}: {
  entry: CalendarEntry;
  /** ISO day this segment is rendered on. */
  day: string;
  onDelete: () => void;
  /** Opens the edit dialog — hotel entries only; global calendar rows are read-only. */
  onOpen: () => void;
}) {
  const s = kindStyle(entry.kind);
  const editable = entry.source === 'hotel';
  const position = spanPosition(entry, day);
  const labelled = position === 'single' || showsSpanTitle(entry, day);
  const dateLabel =
    entry.endsOn && entry.endsOn !== entry.startsOn
      ? `${entry.startsOn} – ${entry.endsOn}`
      : entry.startsOn;
  // Bleed into the cell's 1.5 padding on the joined sides so consecutive days
  // form an unbroken bar instead of separate pills.
  const joinLeft = position === 'middle' || position === 'end';
  const joinRight = position === 'middle' || position === 'start';
  return (
    <div
      className={cn(
        'group/pill flex items-center gap-1 truncate py-0.5 text-meta font-medium leading-tight',
        joinLeft ? '-ml-1.5 rounded-l-none pl-1.5' : 'rounded-l pl-1.5',
        joinRight ? '-mr-1.5 rounded-r-none pr-1.5' : 'rounded-r pr-1.5',
      )}
      style={{ background: s.bg, color: s.fg }}
      title={[`${s.label} · ${entry.title}`, dateLabel, entry.description]
        .filter(Boolean)
        .join('\n')}
    >
      {editable ? (
        <button
          type="button"
          onClick={onOpen}
          className="truncate text-left underline-offset-2 hover:underline"
          aria-label={
            labelled
              ? `${entry.title} megnyitása szerkesztésre`
              : `${entry.title} (folytatás) megnyitása szerkesztésre`
          }
        >
          {/* Continuation segments stay blank so the bar reads as one event. */}
          {labelled ? entry.title : '\u00a0'}
        </button>
      ) : (
        <span className="truncate">{labelled ? entry.title : '\u00a0'}</span>
      )}
      {editable && labelled && (
        <button
          type="button"
          onClick={onDelete}
          className="ml-auto hidden opacity-70 hover:opacity-100 group-hover/pill:inline"
          aria-label="Törlés"
        >
          ×
        </button>
      )}
    </div>
  );
}

function AddEventModal({
  hotelId,
  date,
  onClose,
  onCreated,
  onOpenImport,
}: {
  hotelId: string;
  date: string;
  onClose: () => void;
  onCreated: () => void;
  /** Opens the URL/file importer — testers asked for link-based entry from
   *  here, not knowing the importer already existed elsewhere on the page. */
  onOpenImport: () => void;
}) {
  const [endsOn, setEndsOn] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<'event' | 'promo' | 'seasonal'>('event');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => {
      const body: CreateHotelEventBody = {
        startsOn: date,
        title,
        category,
      };
      if (endsOn) body.endsOn = endsOn;
      if (description) body.description = description;
      return createEvent(hotelId, body);
    },
    onSuccess: onCreated,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Új esemény"
        className="w-full max-w-md rounded-lg bg-[var(--app-surface)] p-5 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-h2 font-semibold text-[var(--ink-700)]">
            Új esemény — <span className="tn text-[var(--brand-700)]">{date}</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--fg-2)] hover:text-[var(--ink-700)]"
            aria-label="Bezárás"
          >
            ×
          </button>
        </div>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <div>
            <label className="mb-1 block text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
              Cím
            </label>
            <Input value={title} required onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
                Vége <span className="text-[var(--fg-faint)] normal-case">· opcionális</span>
              </label>
              <Input type="date" value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
                Kategória
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as typeof category)}
                className="h-9 w-full rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-2 text-body"
              >
                <option value="event">esemény</option>
                <option value="promo">akció</option>
                <option value="seasonal">szezon</option>
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-meta font-semibold uppercase tracking-wider text-[var(--fg-2)]">
              Leírás <span className="text-[var(--fg-faint)] normal-case">· opcionális</span>
            </label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          {error && <p className="text-meta text-[var(--danger)]">{error}</p>}
          <p className="text-meta text-[var(--fg-2)]">
            Van rá link vagy programfüzet?{' '}
            <button
              type="button"
              onClick={onOpenImport}
              className="font-semibold text-[var(--brand-700)] underline underline-offset-2 hover:text-[var(--brand-800)]"
            >
              Olvasd be weboldalról vagy fájlból
            </button>{' '}
            — egyszerre több eseményt is felvisz, a mezőket kitöltve.
          </p>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose}>
              Mégsem
            </Button>
            <Button type="submit" size="sm" disabled={add.isPending}>
              {add.isPending ? 'Mentés…' : 'Hozzáadás'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function monthBounds(year: number, month: number): { from: string; to: string } {
  const start = new Date(Date.UTC(year, month - 1, 1));
  const end = new Date(Date.UTC(year, month, 0));
  return { from: toIso(start), to: toIso(end) };
}

function toIso(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function buildMonthGrid(
  year: number,
  month: number,
): { iso: string; day: number; inMonth: boolean; isToday: boolean }[] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  // Monday-first: 0=Mon..6=Sun
  const firstDow = (first.getUTCDay() + 6) % 7;
  const start = new Date(first);
  start.setUTCDate(first.getUTCDate() - firstDow);
  const days: { iso: string; day: number; inMonth: boolean; isToday: boolean }[] = [];
  const todayIso = toIso(new Date());
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    const iso = toIso(d);
    days.push({
      iso,
      day: d.getUTCDate(),
      inMonth: d.getUTCMonth() === month - 1,
      isToday: iso === todayIso,
    });
  }
  return days;
}

export const Route = createFileRoute('/app/hotels/$hotelId/events')({
  component: HotelEventsPage,
});
